import { z } from "zod";
import { PlanningOpaqueIdSchema, PlanningRequestIdSchema } from "./planning";
import {
  RoutineAvailabilitySchema,
  RoutineEvidencePolicySchema,
  RoutineNotificationPolicySchema,
  RoutineScheduleSchema,
} from "./routines";

export const RoutineTemplateInputSchema = z
  .object({
    name: z.string().trim().min(1).max(160),
    child_copy: z.string().trim().min(1).max(160),
    notes: z.string().trim().max(8000).default(""),
    color: z
      .string()
      .regex(/^#[0-9a-fA-F]{6}$/u)
      .default("#4f8f72"),
    schedule: RoutineScheduleSchema,
    availability: RoutineAvailabilitySchema.default({
      period: "anytime",
      from_local_time: null,
      until_local_time: null,
    }),
    due_offset_days: z.number().int().min(0).max(30).default(0),
    default_duration_minutes: z.number().int().min(0).max(1440).nullable().default(null),
    verification_policy: z.literal("none").default("none"),
    evidence_policy: z
      .object({ required: z.literal("none").default("none") })
      .strict()
      .default({ required: "none" }),
    notification_policy: RoutineNotificationPolicySchema.default({
      child_reminder: false,
      guardian_digest: false,
    }),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.notification_policy.child_reminder && value.availability.from_local_time === null) {
      context.addIssue({
        code: "custom",
        message: "child reminders require an explicit availability start time",
        path: ["notification_policy", "child_reminder"],
      });
    }
    if (value.notification_policy.guardian_digest && value.availability.until_local_time === null) {
      context.addIssue({
        code: "custom",
        message: "guardian digests require an explicit availability end time",
        path: ["notification_policy", "guardian_digest"],
      });
    }
  });

export const RoutineTemplateSchema = z
  .object({
    ...RoutineTemplateInputSchema.shape,
    id: PlanningOpaqueIdSchema,
    workspace_id: PlanningOpaqueIdSchema,
    color: z.string().regex(/^#[0-9a-f]{6}$/u),
    availability: RoutineAvailabilitySchema,
    verification_policy: z.literal("none"),
    evidence_policy: RoutineEvidencePolicySchema,
    notification_policy: RoutineNotificationPolicySchema,
    version: z.number().int().min(1),
    created_at: z.number().int(),
    updated_at: z.number().int(),
    archived_at: z.number().int().nullable(),
  })
  .strict();

export const CreateRoutineTemplateRequestSchema = RoutineTemplateInputSchema;

export const UpdateRoutineTemplateRequestSchema = z
  .object({
    ...RoutineTemplateInputSchema.shape,
    expected_version: z.number().int().min(1),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.notification_policy.child_reminder && value.availability.from_local_time === null) {
      context.addIssue({
        code: "custom",
        message: "child reminders require an explicit availability start time",
        path: ["notification_policy", "child_reminder"],
      });
    }
    if (value.notification_policy.guardian_digest && value.availability.until_local_time === null) {
      context.addIssue({
        code: "custom",
        message: "guardian digests require an explicit availability end time",
        path: ["notification_policy", "guardian_digest"],
      });
    }
  });

export const RoutineTemplateListResponseSchema = z
  .object({
    items: z.array(RoutineTemplateSchema).max(200),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export const RoutineTemplateMutationResponseSchema = z
  .object({
    template: RoutineTemplateSchema,
    idempotency_replayed: z.boolean(),
    sync_cursor: z.string().min(1),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export const RoutineTemplateArchiveCommandRequestSchema = z
  .object({ expected_version: z.number().int().min(1) })
  .strict();

export const RoutineTemplateArchiveCommandResponseSchema = z
  .object({
    template: RoutineTemplateSchema,
    command: z.enum(["archive", "restore"]),
    idempotency_replayed: z.boolean(),
    sync_cursor: z.string().min(1),
    request_id: PlanningRequestIdSchema,
  })
  .strict();
